import type { AnnotationQuote } from '../annotationTypes';
import { createAnnotationId } from '../annotationTypes';

export interface CapturedSelection {
  id: string;
  quote: AnnotationQuote;
  rect: DOMRect;
  selectedText: string;
  timestamp: number;
}

function getRangeRect(range: Range) {
  if (typeof range.getBoundingClientRect === 'function') {
    return range.getBoundingClientRect();
  }

  return DOMRect.fromRect({
    height: 0,
    width: 0,
    x: 0,
    y: 0,
  });
}

function clampContextWindow(
  text: string,
  startOffset: number,
  endOffset: number,
  windowSize: number,
) {
  return {
    prefix: text.slice(Math.max(0, startOffset - windowSize), startOffset),
    suffix: text.slice(endOffset, Math.min(text.length, endOffset + windowSize)),
  };
}

export function getSelectionOffsetsWithinContainer(
  container: HTMLElement,
  range: Range,
) {
  const preSelectionRange = document.createRange();
  preSelectionRange.selectNodeContents(container);
  preSelectionRange.setEnd(range.startContainer, range.startOffset);

  const selectedText = range.toString();
  const startOffset = preSelectionRange.toString().length;
  const endOffset = startOffset + selectedText.length;

  if (selectedText.length === 0) {
    return null;
  }

  return {
    endOffset,
    selectedText,
    startOffset,
  };
}

export function captureSelectionQuote(
  container: HTMLElement,
  selection: Selection,
  contextWindow = 32,
): CapturedSelection | null {
  if (selection.rangeCount === 0 || selection.isCollapsed) {
    return null;
  }

  const range = selection.getRangeAt(0);

  if (!container.contains(range.commonAncestorContainer)) {
    return null;
  }

  const selectedText = range.toString();

  if (selectedText.trim().length === 0) {
    return null;
  }

  const offsets = getSelectionOffsetsWithinContainer(container, range);

  if (!offsets) {
    return null;
  }

  const containerText = container.textContent?.replace(/\u00a0/g, ' ') ?? '';
  const context = clampContextWindow(
    containerText,
    offsets.startOffset,
    offsets.endOffset,
    contextWindow,
  );

  return {
    id: createAnnotationId(),
    quote: {
      endOffset: offsets.endOffset,
      exact: selectedText,
      prefix: context.prefix,
      startOffset: offsets.startOffset,
      suffix: context.suffix,
    },
    rect: getRangeRect(range),
    selectedText,
    timestamp: Date.now(),
  };
}
